/**
 * Competitions.
 *
 * This is the single most important correction Kate made to her own design. Asked how
 * you win, she first said "survive the longest", then reversed it herself:
 *
 *   "Well, actually, there's no winning the games. Remember the competitions?
 *    Competitions are little mini games that you win. Win by killing each other, doing
 *    obstacle courses, hide and go seek. You can hide from each other."
 *
 * So the *game* has no win condition — you survive and have fun being different animals.
 * Competitions are the only thing you win, and winning one is the only source of a
 * squishy card (M8).
 *
 * She also called out that a chameleon is a good hide-and-seek animal, so camouflage has
 * to actually matter in that mode.
 */
import type { AnimalAgent } from '../animals/agent'
import { has, type Species } from '../animals/species'

export type CompetitionKind = 'kill-match' | 'obstacle-course' | 'hide-and-seek'

export interface CompetitionSpec {
  kind: CompetitionKind
  name: string
  description: string
  /** Seconds the competition runs before it is decided. */
  duration: number
}

export const COMPETITIONS: Record<CompetitionKind, CompetitionSpec> = {
  'kill-match': {
    kind: 'kill-match',
    name: 'Kill match',
    description: 'Most kills when the clock runs out wins.',
    duration: 180,
  },
  'obstacle-course': {
    kind: 'obstacle-course',
    name: 'Obstacle course',
    description: 'Touch every checkpoint in order. Fastest wins.',
    duration: 240,
  },
  'hide-and-seek': {
    kind: 'hide-and-seek',
    name: 'Hide and seek',
    description: 'One seeker. Stay hidden until the clock runs out.',
    duration: 210,
  },
}

export const COMPETITION_ORDER: CompetitionKind[] = [
  'kill-match', 'obstacle-course', 'hide-and-seek',
]

/** How close you must be to a checkpoint to bank it. */
export const CHECKPOINT_RADIUS = 3
/** How close a seeker must get to find someone hiding. */
const SEEK_RADIUS = 6
/** Camouflage shrinks the radius a seeker can spot you at, down to this fraction. */
const CAMOUFLAGE_PROTECTION = 0.18

export interface Checkpoint {
  x: number
  z: number
  reached: boolean
}

export interface CompetitionResult {
  kind: CompetitionKind
  /** True when the local player won. */
  won: boolean
  detail: string
}

export class Competition {
  readonly kind: CompetitionKind
  readonly spec: CompetitionSpec
  timeLeft: number
  finished = false
  result: CompetitionResult | null = null

  /** Kill match. */
  kills = 0
  rivalBestKills = 0

  /** Obstacle course. */
  readonly checkpoints: Checkpoint[] = []
  /** Seconds the local player took, once every checkpoint is banked. */
  finishedAt: number | null = null

  /** Hide and seek. */
  seeker: AnimalAgent | null = null
  foundAt: number | null = null

  private elapsed = 0
  private readonly random: () => number

  constructor(kind: CompetitionKind, seed = 4242) {
    this.kind = kind
    this.spec = COMPETITIONS[kind]
    this.timeLeft = this.spec.duration

    let a = seed >>> 0
    this.random = () => {
      a = (a + 0x6d2b79f5) >>> 0
      let t = Math.imul(a ^ (a >>> 15), 1 | a)
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }

    if (kind === 'obstacle-course') {
      for (let i = 0; i < 6; i++) {
        const angle = (i / 6) * Math.PI * 2 + this.random() * 0.6
        const radius = 12 + this.random() * 26
        this.checkpoints.push({
          x: Math.cos(angle) * radius,
          z: Math.sin(angle) * radius,
          reached: false,
        })
      }
    }
  }

  /** The next checkpoint to touch, or null when they're all banked. */
  get nextCheckpoint(): Checkpoint | null {
    return this.checkpoints.find((c) => !c.reached) ?? null
  }

  get checkpointsReached(): number {
    return this.checkpoints.filter((c) => c.reached).length
  }

  /**
   * Advance one fixed step.
   *
   * `hidden` is the local player's camouflage strength, 0..1 — Kate named the chameleon
   * as a hide-and-seek animal, so it shortens the distance a seeker can spot you at.
   */
  update(
    dt: number,
    playerX: number,
    playerZ: number,
    species: Species,
    hidden: number,
    animals: readonly AnimalAgent[],
  ): void {
    if (this.finished) return
    this.elapsed += dt
    this.timeLeft = Math.max(0, this.timeLeft - dt)

    switch (this.kind) {
      case 'kill-match': {
        // Rivals score too, so winning isn't automatic.
        if (this.random() < dt / 22) this.rivalBestKills++
        break
      }

      case 'obstacle-course': {
        const next = this.nextCheckpoint
        // In order — Kate's course is a sequence, not a scavenger hunt.
        if (next && Math.hypot(next.x - playerX, next.z - playerZ) <= CHECKPOINT_RADIUS) {
          next.reached = true
          if (!this.nextCheckpoint) this.finishedAt = this.elapsed
        }
        break
      }

      case 'hide-and-seek': {
        if (!this.seeker) {
          this.seeker = animals.find((a) => !a.isDead()) ?? null
        }
        if (this.seeker && !this.seeker.isDead() && this.foundAt === null) {
          const distance = Math.hypot(this.seeker.x - playerX, this.seeker.z - playerZ)
          // Camouflage is the difference between being seen and not.
          const canSpotAt = SEEK_RADIUS * (1 - hidden * (1 - CAMOUFLAGE_PROTECTION))
          if (distance <= canSpotAt) this.foundAt = this.elapsed
        }
        break
      }
    }

    if (this.timeLeft <= 0 || this.isDecidedEarly()) this.finish(species)
  }

  private isDecidedEarly(): boolean {
    if (this.kind === 'obstacle-course') return this.finishedAt !== null
    if (this.kind === 'hide-and-seek') return this.foundAt !== null
    return false
  }

  private finish(species: Species): void {
    this.finished = true
    switch (this.kind) {
      case 'kill-match':
        this.result = {
          kind: this.kind,
          won: this.kills > this.rivalBestKills,
          detail: `${this.kills} kills to ${this.rivalBestKills}`,
        }
        break
      case 'obstacle-course':
        this.result = {
          kind: this.kind,
          won: this.finishedAt !== null,
          detail: this.finishedAt !== null
            ? `finished in ${this.finishedAt.toFixed(1)}s`
            : `only ${this.checkpointsReached} of ${this.checkpoints.length} checkpoints`,
        }
        break
      case 'hide-and-seek':
        this.result = {
          kind: this.kind,
          won: this.foundAt === null,
          detail: this.foundAt === null
            ? `stayed hidden${has(species, 'camouflage') ? ' (camouflaged)' : ''}`
            : `found after ${this.foundAt.toFixed(0)}s`,
        }
        break
    }
  }

  /** Record a kill scored by the local player. */
  recordKill(): void {
    if (!this.finished && this.kind === 'kill-match') this.kills++
  }
}
